import type { ControllerProps, FieldPath, FieldValues } from "react-hook-form";

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";

import { cn } from "@/lib/utils";

export type FormTextareaProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<ControllerProps<TFieldValues, TName>, "render"> & {
  label?: string;
  placeholder?: string;
  description?: string;
  wrapperClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  descriptionClassName?: string;
  messageClassName?: string;
  rows?: number;
  maxLength?: number;
  required?: boolean;
  disabled?: boolean;
  preserveMessageSpace?: boolean;
};

export const FormTextarea = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  label,
  name,
  placeholder,
  description,
  wrapperClassName,
  labelClassName,
  inputClassName,
  descriptionClassName,
  messageClassName,
  rows = 10,
  maxLength,
  required,
  disabled,
  preserveMessageSpace = false,
  ...props
}: FormTextareaProps<TFieldValues, TName>) => {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem
          className={wrapperClassName}
          aria-invalid={!!fieldState?.error}
        >
          {label && (
            <FormLabel
              className={cn(
                labelClassName,
                required && "aero:flex aero:items-center aero:justify-between"
              )}
            >
              {label}{" "}
              {required && (
                <span className="aero:text-destructive" aria-hidden="true">
                  *
                </span>
              )}
            </FormLabel>
          )}
          <FormControl>
            <Textarea
              className={cn(
                "aero:font-inter aero:font-normal aero:text-sm aero:leading-5 aero:text-foreground aero:placeholder:text-neutral-500",
                fieldState?.error ? "aero:border-destructive" : "",
                inputClassName
              )}
              rows={rows}
              placeholder={placeholder}
              disabled={disabled}
              maxLength={maxLength}
              {...field}
            />
          </FormControl>
          {description && (
            <FormDescription className={descriptionClassName}>
              {description}
            </FormDescription>
          )}
          <div className={cn(preserveMessageSpace ? "aero:min-h-[18px]" : "")}>
            <FormMessage
              className={cn(
                fieldState?.error
                  ? "aero:text-destructive aero:text-xs"
                  : preserveMessageSpace
                    ? "aero:invisible aero:text-xs"
                    : "aero:hidden",
                messageClassName
              )}
            />
          </div>
        </FormItem>
      )}
      {...props}
    />
  );
};
